import type { WidgetConfig } from "../api";
import { launcherHasText, PRESETS, type PresetId } from "../../../chat-widget/src/presets";

/**
 * The appearance form's decisions, kept out of the component so they can be
 * tested without a browser.
 */

/** Fills the look fields a server from before presets would not have sent. */
export function withLookDefaults(config: WidgetConfig): WidgetConfig {
  return {
    ...config,
    quickReplies: config.quickReplies ?? [],
    faq: config.faq ?? [],
    preset: config.preset ?? "classic",
    radius: config.radius ?? null,
    font: config.font ?? "preset",
    launcherStyle: config.launcherStyle ?? "preset",
    launcherText: config.launcherText ?? "",
  };
}

/**
 * Picking a card in the gallery. The site's brand colours stay unless the
 * owner asked for the preset's own — a preset is a shell, and most sites
 * already have a colour they are not about to give up.
 */
export function selectPreset(config: WidgetConfig, preset: PresetId, applyColours: boolean): WidgetConfig {
  const next = { ...config, preset };
  if (!applyColours) return next;
  return { ...next, primaryColor: PRESETS[preset].primary, onPrimaryColor: PRESETS[preset].onPrimary };
}

/** Whether the site's colours already are the preset's suggestion. */
export function usesSuggestedColours(config: WidgetConfig): boolean {
  const info = PRESETS[config.preset] ?? PRESETS.classic;
  return (
    config.primaryColor.toLowerCase() === info.primary.toLowerCase() &&
    config.onPrimaryColor.toLowerCase() === info.onPrimary.toLowerCase()
  );
}

/** The radius slider's "preset default" box: unticking starts from a sensible middle. */
export function setRadius(config: WidgetConfig, radius: number | null): WidgetConfig {
  return { ...config, radius: radius === null ? null : Math.max(0, Math.min(28, Math.round(radius))) };
}

/** The launcher text field only means something for a launcher with words on it. */
export function showsLauncherText(config: WidgetConfig): boolean {
  return launcherHasText(config);
}

/** The config the gallery card for `preset` is drawn with: the preset's own colours, the site's theme. */
export function cardConfig(config: WidgetConfig, preset: PresetId): WidgetConfig {
  return {
    ...selectPreset(config, preset, true),
    radius: null,
    font: "preset",
    launcherStyle: "preset",
    launcherText: "",
    position: "bottom-right",
    offsetX: 20,
    offsetY: 20,
  };
}
